import {
  Link,
  Sidebar,
  SidebarGroup,
  SidebarPage,
  SidebarSpace,
  sidebarConfig,
  useSidebarOpenState,
  type BackstageOverrides,
} from '@backstage/core-components';
import { BUIProvider } from '@backstage/ui';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { Settings as SidebarSettings } from '@backstage/plugin-user-settings';
import {
  makeStyles,
  styled,
  ThemeProvider,
  type Theme,
} from '@material-ui/core';
import CreateComponentIcon from '@mui/icons-material/AddCircleOutline';
import MenuIcon from '@mui/icons-material/Menu';
import MoodIcon from '@mui/icons-material/Mood';
import BuildIcon from '@mui/icons-material/Build';
import SearchIcon from '@mui/icons-material/Search';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import HandymanIcon from '@mui/icons-material/Handyman';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import AppsIcon from '@mui/icons-material/Apps';
import { SidebarItem } from '@traton/devportal-env-kit-app';
import { type MouseEvent, type PropsWithChildren, useEffect } from 'react';
import {
  AlternativeStartPageSidebarItem,
  ChatProvider,
  DeveloperAssistantSidebarItem,
  requestDeveloperAssistantHomeNavigation,
} from '@internal/backstage-plugin-developer-portal-support-agent';
import {
  AnnouncementBanner,
  AnnouncementSidebarItem,
} from '@internal/backstage-plugin-announcement';
import { PortalgroupsSidebarItem } from '@internal/backstage-plugin-portalgroups';
import { AgenticMarketplaceSidebarItem } from '@internal/backstage-plugin-agentic-marketplace';
import { TechDocsFeedbackSidebarItem } from '@internal/plugin-techdocs-feedback';
import EnvSticker from '../env-sticker/EnvSticker';
import { Logo } from '../logo';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { devToolsAdministerPermission } from '@backstage/plugin-devtools-common';
import { VtabSidebarItem } from '@traton/vtab-plugin';
import { announcementCreatePermission } from '@internal/backstage-plugin-announcement-common';
import { VendingMachineSidebarItem } from '@traton/backstage-plugin-vending-machine-frontend';

// SidebarPage uses the shared config to reserve space for the pinned sidebar.
// Set the width here so the drawer and page layout stay in sync.
sidebarConfig.drawerWidthOpen = 264;

// Theme slot overrides resolve to the generated classes in both dev and production.
type SidebarTheme = Theme & { overrides?: BackstageOverrides };

const sidebarTheme = (theme: SidebarTheme): SidebarTheme => ({
  ...theme,
  overrides: {
    ...theme.overrides,
    BackstageSidebarItem: {
      ...theme.overrides?.BackstageSidebarItem,
      root: {
        ...theme.overrides?.BackstageSidebarItem?.root,
        'nav[aria-label="sidebar nav"] &$open': {
          boxSizing: 'border-box',
          paddingRight: 'var(--bui-space-5)',
        },
      },
      secondaryAction: {
        ...theme.overrides?.BackstageSidebarItem?.secondaryAction,
        // Backstage renders this slot even when there is no secondary action.
        'nav[aria-label="sidebar nav"] &:empty': {
          display: 'none',
        },
      },
    },
  },
});

const PortalSidebar = ({ children }: PropsWithChildren<{}>) => (
  <ThemeProvider theme={sidebarTheme}>
    <Sidebar>{children}</Sidebar>
  </ThemeProvider>
);

const SidebarDivider = styled('hr')(({ theme }) => ({
  height: 1,
  width: '100%',
  background: '#8C8A84',
  border: 'none',
  margin: theme.spacing(1, 0),
}));

const SettingsContainer = styled('div')(() => ({
  margin: '-1px 0px 9px 0px',
}));

const SearchContainer = styled('div')(() => ({
  '& .search-icon': {
    borderRadius: 0,
    transition: 'none',
  },
}));

const SidebarLogo = () => {
  const { isOpen } = useSidebarOpenState();

  const handleHomeClick = (event: MouseEvent<HTMLAnchorElement>) => {
    // This sends a click event to the developer assistant component to reset the conversation state
    // if the TRATON logo is clicked. It'll be abrupted if the event is cancelled by an event listener.
    // For example when a chat is streaming, the event is abrupted and a dialog shows instead.
    if (!requestDeveloperAssistantHomeNavigation()) {
      event.preventDefault();
    }
  };

  const classes = makeStyles({
    root: {
      width: sidebarConfig.drawerWidthClosed,
      height: 3 * sidebarConfig.logoHeight - 1,
      display: 'flex',
      flexFlow: 'row nowrap',
      alignItems: 'center',
      marginBottom: -14,
    },
    link: {
      width: sidebarConfig.drawerWidthClosed,
      marginLeft: isOpen ? 33 : 22,
      marginTop: isOpen ? 11 : 11,
    },
  })();

  return (
    <div className={classes.root}>
      <Link
        to="/"
        underline="none"
        className={classes.link}
        aria-label="Home"
        onClick={handleHomeClick}
      >
        <Logo isOpen={isOpen} />
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  // Backstage's Page component defaults to "height: 100vh; overflow-y: auto",
  // creating its own scroll container. This conflicts with:
  //   1. The in-flow AnnouncementBanner (causes double scroll when banner is shown)
  //   2. Plugins like agentic-marketplace that manage their own internal scroll
  //      containers sized relative to the Page grid — they rely on the Page
  //      having no fixed height so their height: 100% resolves to content height.
  //
  // We override unconditionally:
  //   height: auto        — cancels the fixed 100vh, lets the Page grow with content
  //   min-height: 100vh   — ensures the Page still fills the full viewport when
  //                         content is shorter than the screen
  //   overflow-y: visible — removes the Page's internal scroll, all scrolling is
  //                         handled by the browser body or inner components
  useEffect(() => {
    const id = 'announcement-page-height-override';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.textContent = `[data-backstage-core-page] { height: auto !important; min-height: 100vh !important; overflow-y: visible !important; }`;
      document.head.appendChild(style);
    }
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  return (
    <BUIProvider>
      <SidebarPage>
        <EnvSticker />
        <PortalSidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SearchContainer>
              <SidebarSearchModal />
            </SearchContainer>
          </SidebarGroup>
          <DeveloperAssistantSidebarItem />
          <AlternativeStartPageSidebarItem />
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            <SidebarItem
              icon={CategoryOutlinedIcon}
              to="catalog"
              text="Catalog"
            />
            <SidebarItem
              icon={ExtensionOutlinedIcon}
              to="api-docs"
              text="APIs"
            />
            <SidebarItem
              icon={LibraryBooksOutlinedIcon}
              to="/docs"
              text="Documentation"
            />
            <SidebarItem
              icon={RouteOutlinedIcon}
              to="/golden-paths?filters%5Btags%5D=golden-path&filters%5Buser%5D=all&limit=20"
              text="Golden Paths"
            />

            <SidebarItem
              icon={CreateComponentIcon}
              to="create"
              text="Create..."
            />
            <PortalgroupsSidebarItem />
            <SidebarItem
              icon={HandymanIcon}
              to="/tooling"
              text="Developer Tooling"
            />
            <SidebarItem
              icon={AccountTreeOutlinedIcon}
              to="/docs/default/component/ee-process-documentation"
              text="R&D Embedded Processes"
              title="R&D Embedded Processes"
            />
            <TechDocsFeedbackSidebarItem />
            <SidebarDivider />
            <AgenticMarketplaceSidebarItem />
            <VtabSidebarItem />
            <SidebarItem
              icon={AppsIcon}
              pluginId="oas-launchpad"
              to="/oas-launchpad"
              text="OAS Launchpad"
            />
            <SidebarItem
              icon={MoodIcon}
              to="/tvip"
              text="TVIP"
              pluginId="tvip"
            />
            <VendingMachineSidebarItem />
            <SidebarDivider />
            <SidebarItem icon={MoodIcon} to="/xkcd" text="Comics" />
          </SidebarGroup>

          <SidebarSpace />
          <SidebarDivider />
          <RequirePermission
            permission={announcementCreatePermission}
            errorPage={<></>}
          >
            <SidebarGroup label="Announcement">
              <AnnouncementSidebarItem />
            </SidebarGroup>
          </RequirePermission>
          <RequirePermission
            permission={devToolsAdministerPermission}
            errorPage={<></>}
          >
            <SidebarGroup label="DevTools" icon={<BuildIcon />} to="/devtools">
              <SidebarItem icon={BuildIcon} to="devtools" text="DevTools" />
            </SidebarGroup>
          </RequirePermission>
          <SidebarGroup
            label="Settings"
            icon={<SettingsOutlinedIcon />}
            to="/settings"
          >
            <SettingsContainer>
              <SidebarSettings icon={SettingsOutlinedIcon} />
            </SettingsContainer>
          </SidebarGroup>
        </PortalSidebar>
        <ChatProvider>
          <AnnouncementBanner />
          {children}
        </ChatProvider>
      </SidebarPage>
    </BUIProvider>
  );
};
